import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import { fetchSiteLookup, staffMappedToAnySite } from "../../../services/lookupService";
import { guidOrNull } from "../../../utils/guidUtils";
import {
  croHasWholeStudyIsfGrant,
  getCroGrantedSiteIdsForStudy,
  isSponsorCroSession,
} from "../../../utils/sponsorCroAccess";

export function defaultReferenceDateValue(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function parseReferenceDateInput(value) {
  if (!value) return null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) return value;
  const s = String(value).trim();
  const iso = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    const d = new Date(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const dmy = s.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) {
    const d = new Date(Number(dmy[3]), Number(dmy[2]) - 1, Number(dmy[1]));
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatReferenceDateForApi(value) {
  if (!value) return "";
  const d = parseReferenceDateInput(value);
  if (!d) return "";
  return defaultReferenceDateValue(d);
}

export function hasReferenceDateValue(value) {
  return Boolean(formatReferenceDateForApi(value));
}

export function buildInitialPerSiteReferenceDates(siteRows = [], fallbackDate) {
  const value = fallbackDate || defaultReferenceDateValue();
  const initial = {};
  (siteRows || []).forEach((row) => {
    if (row?.SiteId) initial[row.SiteId] = value;
  });
  return initial;
}

function extractRows(response, tableName) {
  const tables = response?.data?.Tables || response?.data?.tables;
  if (Array.isArray(tables)) {
    const match = tables.find(
      (t) => String(t?.TableName || t?.tableName || "").toLowerCase() === tableName.toLowerCase(),
    );
    if (Array.isArray(match?.Rows)) return match.Rows;
    if (Array.isArray(match?.rows)) return match.rows;
  }
  const direct = response?.data?.[tableName] ?? response?.data?.[tableName.toLowerCase()];
  if (Array.isArray(direct)) return direct;
  return [];
}

export function getUniformPerSiteTemplate(siteRows = [], perSiteTemplates = {}) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (rows.length <= 1) return null;

  let shared = null;
  for (const row of rows) {
    const selected = perSiteTemplates?.[row.SiteId];
    const templateId = selected?.value;
    if (!templateId) return null;
    if (!shared) {
      shared = selected;
    } else if (String(shared.value) !== String(templateId)) {
      return null;
    }
  }
  return shared;
}

/** Update per-site template selection; caller keeps UI mode unchanged. */
export function resolvePerSiteTemplateSelection(
  siteRows = [],
  perSiteTemplates = {},
  siteId,
  opt,
) {
  const next = { ...perSiteTemplates, [siteId]: opt || null };
  const uniform = getUniformPerSiteTemplate(siteRows, next);

  return {
    perSiteTemplates: next,
    uniformTemplate: uniform,
    allSitesUniform: Boolean(uniform),
  };
}

export function getUniformTemplateNotice(
  siteRows = [],
  perSiteTemplates = {},
  sameTemplateForAll = true,
) {
  if (sameTemplateForAll) return "";
  const uniform = getUniformPerSiteTemplate(siteRows, perSiteTemplates);
  if (!uniform?.label) return "";
  return `All sites will use "${uniform.label}". This will be saved as the same template for all sites.`;
}

export function buildStudySiteSetupPayload({
  siteRows = [],
  sameTemplateForAllSites = true,
  sharedTemplateId = null,
  perSiteTemplates = {},
  sharedReferenceDate = null,
  perSiteReferenceDates = {},
}) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  const singleSite = rows.length <= 1;
  const uniform = sameTemplateForAllSites
    ? null
    : getUniformPerSiteTemplate(rows, perSiteTemplates);
  const effectiveSameTemplate =
    singleSite || sameTemplateForAllSites || Boolean(uniform);
  const defaultTemplateId = effectiveSameTemplate
    ? sharedTemplateId || uniform?.value || null
    : null;
  const defaultReferenceDate = formatReferenceDateForApi(
    sharedReferenceDate || defaultReferenceDateValue(),
  );

  const sites = rows.map((site) => ({
    SiteId: site.SiteId,
    TemplateId: effectiveSameTemplate
      ? defaultTemplateId
      : perSiteTemplates[site.SiteId]?.value || uniform?.value || null,
    ReferenceDate: formatReferenceDateForApi(
      perSiteReferenceDates[site.SiteId] ||
        (effectiveSameTemplate ? defaultReferenceDate : null) ||
        defaultReferenceDate,
    ),
  }));

  return {
    sameTemplateForAllSites: effectiveSameTemplate,
    defaultTemplateId: effectiveSameTemplate ? defaultTemplateId : null,
    sites,
  };
}

export function validateStudySiteSetup({
  siteRows = [],
  sameTemplateForAllSites = true,
  sharedTemplateId = null,
  perSiteTemplates = {},
  sharedReferenceDate = null,
  perSiteReferenceDates = {},
}) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (rows.length <= 1) {
    if (!sharedTemplateId) {
      return { valid: false, message: "Please select a template" };
    }
    if (!hasReferenceDateValue(sharedReferenceDate)) {
      return { valid: false, message: "Please select a reference date" };
    }
    return { valid: true };
  }

  if (sameTemplateForAllSites) {
    if (!sharedTemplateId) {
      return { valid: false, message: "Please select a template for all sites" };
    }
    if (!hasReferenceDateValue(sharedReferenceDate)) {
      return { valid: false, message: "Please select a reference date for all sites" };
    }
    return { valid: true };
  }

  const uniform = getUniformPerSiteTemplate(rows, perSiteTemplates);
  if (uniform) {
    if (!hasReferenceDateValue(sharedReferenceDate)) {
      return { valid: false, message: "Please select a reference date for all sites" };
    }
    return { valid: true };
  }

  const missingTemplate = rows.some((site) => !perSiteTemplates[site.SiteId]?.value);
  if (missingTemplate) {
    return { valid: false, message: "Please select a template for every site" };
  }

  const missingReferenceDate = rows.some(
    (site) => !hasReferenceDateValue(perSiteReferenceDates[site.SiteId]),
  );
  if (missingReferenceDate) {
    return { valid: false, message: "Please select a reference date for every site" };
  }

  return { valid: true };
}

export function isEisfApiSuccess(response) {
  return (
    response?.success === 1 ||
    response?.success === true ||
    response?.success === "1"
  );
}

export function normalizeStudyFolderResponseData(data) {
  if (!data || typeof data !== "object") return {};
  if (
    Array.isArray(data.StudyFolder) ||
    Array.isArray(data.FolderDocuments) ||
    Array.isArray(data.StaffListRecord)
  ) {
    return data;
  }

  const tables = data.Tables || data.tables;
  if (!Array.isArray(tables)) return data;

  const byName = {};
  tables.forEach((table) => {
    const name = table?.TableName || table?.tableName;
    const rows = table?.Rows || table?.rows || [];
    if (name) byName[name] = rows;
  });

  return {
    ...data,
    StudyFolder: byName.StudyFolder || [],
    StaffListRecord: byName.StaffListRecord || [],
    StaffStudyDetails: byName.StaffStudyDetails || [],
    RestrictedFolders: byName.RestrictedFolders || [],
    FolderDocuments: byName.FolderDocuments || [],
  };
}

export function extractApiTableRows(data, tableName) {
  if (!data || typeof data !== "object") return [];

  const target = String(tableName || "").toLowerCase();
  const directKey = Object.keys(data).find(
    (key) => String(key).toLowerCase() === target,
  );
  if (directKey && Array.isArray(data[directKey])) return data[directKey];

  const tables = data.Tables || data.tables;
  if (Array.isArray(tables)) {
    const match = tables.find(
      (table) =>
        String(table?.TableName || table?.tableName || "").toLowerCase() ===
        target,
    );
    if (Array.isArray(match?.Rows)) return match.Rows;
    if (Array.isArray(match?.rows)) return match.rows;
  }

  return [];
}

export function resolveCreateFlowSiteId(createSites = [], siteContext = null) {
  const createRows = (createSites || []).filter((row) => row.SiteId);
  if (createRows.length >= 1) return createRows[0].SiteId;

  if (siteContext?.defaultSiteId) return siteContext.defaultSiteId;

  const contextRows = (siteContext?.rows || []).filter((row) => row.SiteId);
  if (contextRows.length >= 1) return contextRows[0].SiteId;

  return null;
}

export function resolveCreateFlowTemplateOption({
  isMultiSiteCreate = false,
  sharedTemplateOption = null,
  createSites = [],
  perSiteTemplates = {},
  sameTemplateForAllSites = true,
  templateOptions = [],
}) {
  if (!isMultiSiteCreate) return sharedTemplateOption || null;

  const payload = buildStudySiteSetupPayload({
    siteRows: createSites,
    sameTemplateForAllSites,
    sharedTemplateId: sharedTemplateOption?.value,
    perSiteTemplates,
  });
  const templateId = payload.defaultTemplateId;
  if (!templateId) return null;

  const fromPerSite = Object.values(perSiteTemplates || {}).find(
    (opt) => String(opt?.value) === String(templateId),
  );
  if (fromPerSite) return fromPerSite;

  const fromOptions = (templateOptions || []).find(
    (opt) => String(opt?.value) === String(templateId),
  );
  if (fromOptions) return fromOptions;

  return { value: templateId, label: "Template" };
}

export function formatSiteDisplayLabel(site) {
  const name = String(site?.SiteName || "").trim();
  const number = String(site?.SiteNumber || "").trim();
  if (name && number) return `${name} (${number})`;
  if (name) return name;
  if (number) return number;
  const id = String(site?.SiteId || "").trim();
  return id ? `Site ${id.slice(0, 8)}` : "Site";
}

export function enrichStudySiteRows(siteRows = [], siteLookup = []) {
  const lookup = Array.isArray(siteLookup) ? siteLookup : [];
  return (siteRows || []).map((row) => {
    if (!row?.SiteId) return row;
    const match = lookup.find(
      (site) =>
        String(site.SiteId || site.siteId || "").toLowerCase() ===
        String(row.SiteId).toLowerCase(),
    );
    if (!match) return row;
    return {
      ...row,
      SiteName: row.SiteName || match.SiteName || match.siteName || "",
      SiteNumber: row.SiteNumber || match.SiteNumber || match.siteNumber || "",
      SiteAbbr: row.SiteAbbr || match.SiteAbbr || match.siteAbbr || "",
    };
  });
}

export async function enrichStudySiteRowsFromLookup(siteRows = [], userData) {
  if (!userData?.OrganizationId) return siteRows || [];
  const needsLookup = (siteRows || []).some(
    (row) =>
      row.SiteId &&
      (!String(row.SiteName || "").trim() ||
        !String(row.SiteAbbr || "").trim()),
  );
  if (!needsLookup) return siteRows || [];
  try {
    const siteLookup = await fetchSiteLookup(userData);
    return enrichStudySiteRows(siteRows, siteLookup);
  } catch {
    return siteRows || [];
  }
}

export function normalizeStudySiteRows(response) {
  const rows = extractRows(response, "StudySites");
  return (rows || []).map((row) => ({
    SiteId: String(row.SiteId || row.siteId || "").trim(),
    SiteName: String(row.SiteName || row.siteName || "").trim(),
    SiteNumber: String(row.SiteNumber || row.siteNumber || "").trim(),
    IsConfigured: row.IsConfigured === 1 || row.IsConfigured === true || row.isConfigured === true,
    TemplateId: String(row.TemplateId || row.templateId || "").trim(),
    TemplateName: String(row.TemplateName || row.templateName || "").trim(),
    ConfigStatus: String(row.ConfigStatus || row.configStatus || "").trim(),
  }));
}

export async function fetchStudySitesForSetup({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.STUDY_SITE_CONFIG, {
    ActionType: "GetStudySitesForSetup",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
  });
}

export async function fetchStudySiteConfig({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.STUDY_SITE_CONFIG, {
    ActionType: "GetStudySiteConfig",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
  });
}

export async function applyStudySiteSetup({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
  sameTemplateForAllSites,
  defaultTemplateId,
  sites,
}) {
  return apiClient.post(API_ENDPOINTS.ISF.STUDY_SITE_CONFIG, {
    ActionType: "ApplyStudySiteSetup",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
    SameTemplateForAllSites: Boolean(sameTemplateForAllSites),
    DefaultTemplateId: defaultTemplateId || null,
    Sites: (sites || []).map((row) => ({
      SiteId: row.SiteId,
      TemplateId: row.TemplateId,
      ReferenceDate: row.ReferenceDate || null,
    })),
  });
}

export async function fetchStudyReferenceDates({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.STUDY_REFERENCE_DATE, {
    ActionType: "GetStudyReferenceDates",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
  });
  return extractRows(response, "StudyReferenceDates");
}

export async function saveStudyReferenceDates({
  organizationId,
  studyId,
  templateCategory,
  createdBy,
  sites = [],
  referenceDate = null,
  siteIdRef = null,
}) {
  const formattedFallback = formatReferenceDateForApi(referenceDate);
  const sitePayload = (sites || [])
    .filter((row) => hasReferenceDateValue(row?.ReferenceDate))
    .map((row) => ({
      SiteId: row.SiteId || siteIdRef || null,
      ReferenceDate: formatReferenceDateForApi(row.ReferenceDate),
    }));

  if (!sitePayload.length && formattedFallback) {
    sitePayload.push({
      SiteId: siteIdRef || null,
      ReferenceDate: formattedFallback,
    });
  }

  const primary = sitePayload[0] || null;

  return apiClient.post(API_ENDPOINTS.ISF.STUDY_REFERENCE_DATE, {
    ActionType: "SaveStudyReferenceDates",
    OrganizationIdRef: organizationId,
    StudyIdRef: studyId,
    TemplateCategory: templateCategory || null,
    CreatedBy: createdBy,
    SiteIdRef: primary?.SiteId || siteIdRef || null,
    ReferenceDate: primary?.ReferenceDate || formattedFallback || null,
    Sites: sitePayload,
  });
}

export function studySitesNeedSetup(siteRows = []) {
  if (!Array.isArray(siteRows) || siteRows.length <= 1) return false;
  return siteRows.some((row) => !row.IsConfigured);
}

export function buildSiteSelectOptions(siteRows = []) {
  return (siteRows || [])
    .filter((row) => row.SiteId)
    .map((row) => ({
      value: row.SiteId,
      label: row.SiteNumber
        ? `${row.SiteName || "Site"} (${row.SiteNumber})`
        : row.SiteName || row.SiteId,
    }));
}

/** Configured study sites the current user may access (Stage 2.4). */
export function buildConfiguredSiteSelectOptions(siteRows = [], userData) {
  const configured = (siteRows || []).filter((row) => row.SiteId && row.IsConfigured);
  const allowed = getAllowedStudySites(configured, userData);
  return buildSiteSelectOptions(allowed.length ? allowed : configured);
}

export function getAllowedStudySites(siteRows = [], userData, studyId = null) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (!rows.length) return [];

  if (isSponsorCroSession(userData)) {
    if (croHasWholeStudyIsfGrant(userData, studyId)) return rows;
    const grantedSites = getCroGrantedSiteIdsForStudy(userData, studyId);
    if (grantedSites == null) return rows;
    if (grantedSites.size === 0) return [];
    return rows.filter((row) =>
      grantedSites.has(String(row.SiteId).trim().toLowerCase()),
    );
  }

  const userSiteIds = String(userData?.MappedSiteIds || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);

  if (!userSiteIds.length) return rows;

  return rows.filter((row) => staffMappedToAnySite(userData, [row.SiteId]));
}

export function resolveDefaultStudySiteId(siteRows = [], userData) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (!rows.length) return null;

  const configured = rows.filter((row) => row.IsConfigured);
  const source = configured.length ? configured : rows;
  const allowed = getAllowedStudySites(source, userData);
  const pool = allowed.length ? allowed : source;

  return pool[0]?.SiteId || null;
}

/** Template from EISF_StudySiteConfig for the active site (multi-site studies). */
export function resolveTemplateOptionForSite(siteRows = [], siteId) {
  if (!siteId) return null;
  const row = (siteRows || []).find(
    (r) =>
      r?.SiteId &&
      String(r.SiteId).toLowerCase() === String(siteId).toLowerCase(),
  );
  const templateId = String(row?.TemplateId || "").trim();
  if (!templateId) return null;
  return {
    value: templateId,
    label: row?.TemplateName || "Template",
  };
}

/**
 * Never leave multi-site folder loads without a site id — per-site folders use SiteIdRef.
 */
export function resolveActiveStudySiteId(
  siteRows = [],
  userData,
  preferredSiteId = null,
) {
  const rows = (siteRows || []).filter((row) => row.SiteId);
  if (!rows.length) return preferredSiteId || null;

  if (preferredSiteId) {
    const match = rows.find(
      (r) =>
        String(r.SiteId).toLowerCase() === String(preferredSiteId).toLowerCase(),
    );
    if (match) return match.SiteId;
  }

  if (rows.length > 1) {
    return resolveDefaultStudySiteId(rows, userData);
  }

  return preferredSiteId || rows[0].SiteId;
}
